// Helper functions for metrics data formatting and preparation

// Format uptime in human-readable format
export const formatUptime = (ms) => {
  if (!ms) return 'N/A';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  return `${days}d ${hours % 24}h ${minutes % 60}m ${seconds % 60}s`;
};

// Format bytes to human readable
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date for chart labels
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Prepare data for message processing chart
export const prepareMessageChartData = (metrics) => {
  if (!Array.isArray(metrics) || metrics.length === 0) {
    return {
      labels: [],
      datasets: []
    };
  }

  return {
    labels: metrics.map(m => formatDate(m.timestamp)),
    datasets: [
      {
        label: 'Processed Messages',
        data: metrics.map(m => m.messages_processed || 0),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
      {
        label: 'Matched Messages',
        data: metrics.map(m => m.messages_matched || 0),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
      }
    ],
  };
};

// Prepare data for memory usage chart
export const prepareMemoryChartData = (memoryStats) => {
  // Check if we have history data
  if (memoryStats && memoryStats.history && Array.isArray(memoryStats.history) && memoryStats.history.length > 0) {
    const history = memoryStats.history.slice(-20); // Last 20 data points

    return {
      labels: history.map((_, index) => `${index + 1}`),
      datasets: [
        {
          label: 'Heap Used (MB)',
          data: history.map(h => (h.heapUsed / 1024 / 1024).toFixed(2)),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
        },
        {
          label: 'RSS (MB)',
          data: history.map(h => (h.rss / 1024 / 1024).toFixed(2)),
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
        }
      ],
    };
  }

  // If no history, but we have current data, show single point
  if (memoryStats && memoryStats.process && memoryStats.process.memory) {
    const currentTime = new Date().toLocaleTimeString();
    const memory = memoryStats.process.memory;

    return {
      labels: [currentTime],
      datasets: [
        {
          label: 'Heap Used (MB)',
          data: [(memory.heapUsed / 1024 / 1024).toFixed(2)],
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
        },
        {
          label: 'RSS (MB)',
          data: [(memory.rss / 1024 / 1024).toFixed(2)],
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
        }
      ],
    };
  }

  // No data available
  return {
    labels: [],
    datasets: []
  };
};

// Prepare data for cache performance chart
export const prepareCacheChartData = (currentMetrics) => {
  if (!currentMetrics) return null;

  return {
    labels: ['Hits', 'Misses'],
    datasets: [
      {
        data: [
          currentMetrics.cache_hits || 0,
          currentMetrics.cache_misses || 0
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.5)',
          'rgba(255, 99, 132, 0.5)',
        ],
        borderColor: [
          'rgb(75, 192, 192)',
          'rgb(255, 99, 132)',
        ],
        borderWidth: 1,
      },
    ],
  };
};

// Prepare data for project stats chart
export const prepareProjectChartData = (projectStats) => {
  // Check if projectStats exists and is an array
  if (!projectStats || !Array.isArray(projectStats) || projectStats.length === 0) {
    return {
      labels: [],
      datasets: [
        {
          label: 'Matched Messages',
          data: [],
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgb(54, 162, 235)',
          borderWidth: 1,
        },
      ],
    };
  }

  return {
    labels: projectStats.map(p => p.name || 'Unnamed'),
    datasets: [
      {
        label: 'Matched Messages',
        data: projectStats.map(p => p.matches || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1,
      },
    ],
  };
};

// Prepare data for keyword metrics chart
export const prepareKeywordChartData = (keywordMetrics, type) => {
  if (!keywordMetrics || !keywordMetrics[type] || !Array.isArray(keywordMetrics[type]) || keywordMetrics[type].length === 0) {
    return null;
  }

  // Take top 10 keywords
  const topKeywords = keywordMetrics[type].slice(0, 10);

  return {
    labels: topKeywords.map(k => k.keyword || 'Unknown'),
    datasets: [
      {
        label: `${type.charAt(0).toUpperCase() + type.slice(1)} Keywords`,
        data: topKeywords.map(k => k.count || 0),
        backgroundColor: type === 'primary'
          ? 'rgba(255, 99, 132, 0.5)'
          : type === 'secondary'
            ? 'rgba(54, 162, 235, 0.5)'
            : 'rgba(255, 206, 86, 0.5)',
        borderColor: type === 'primary'
          ? 'rgb(255, 99, 132)'
          : type === 'secondary'
            ? 'rgb(54, 162, 235)'
            : 'rgb(255, 206, 86)',
        borderWidth: 1,
      },
    ],
  };
};
